import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { KeyText } from "./ui";
import { renderCustomerOption, useCustomerOptions } from "./CustomerPicker";
import { brand } from "../theme";
import { productName } from "../config";
import { quotaEntries } from "../lib/licenses";
import { describeIssueError, issueLicense } from "../lib/operations";

const EMPTY = { customer: "", customerEmail: "", location: "", remarks: "" };

function shareText({ licenseKey, product, type, customer, distributorName }) {
  const lines = [
    `Hello${customer ? ` ${customer}` : ""},`,
    "",
    `Here is your ${productName(product)} licence key (${type}):`,
    "",
    licenseKey,
    "",
    "Enter it when you activate your device.",
  ];
  if (distributorName) lines.push(`— ${distributorName}`);
  return lines.join("\n");
}

// Issues one key against a distributor's quota via the issueLicense
// function. Used by distributors themselves and by admins (onBehalf).
// The customer can be picked from the distributor's saved customers, saved
// as a new one, or just typed in (customer records are optional).
export default function IssueKeyDialog({
  open,
  onClose,
  distributor,
  initialBucket,
  initialCustomer,
  onBehalf = false,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const available = useMemo(
    () => quotaEntries(distributor).filter((e) => e.remaining > 0),
    [distributor],
  );
  const [bucket, setBucket] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [customerId, setCustomerId] = useState(null);
  const [linkedCustomer, setLinkedCustomer] = useState(null);
  const [saveCustomer, setSaveCustomer] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      initialCustomer
        ? {
            ...EMPTY,
            customer: initialCustomer.name || "",
            customerEmail: initialCustomer.email || "",
            location: initialCustomer.location || "",
          }
        : EMPTY,
    );
    setCustomerId(initialCustomer?.id || null);
    setLinkedCustomer(initialCustomer || null);
    setSaveCustomer(true);
    setError(null);
    setIssued(null);
    setBucket(initialBucket || "");
    // Only reset when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Default to the first type with keys left.
  const selected =
    available.find((e) => e.bucket === bucket) ||
    (bucket ? null : available[0]) ||
    null;

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));
  // Saved customers matching what's typed, searched in Firestore.
  const { options: customerOptions } = useCustomerOptions(
    distributor?.id ?? undefined,
    customerId ? "" : form.customer,
    open && !issued,
  );

  function pickCustomer(_, value) {
    if (value && typeof value === "object") {
      setCustomerId(value.id);
      setLinkedCustomer(value);
      setForm((f) => ({
        ...f,
        customer: value.name,
        customerEmail: value.email || f.customerEmail,
        location: value.location || f.location,
      }));
    } else {
      setCustomerId(null);
      setLinkedCustomer(null);
    }
  }

  function typeCustomer(_, value, reason) {
    if (reason === "reset" && !value && customerId) return;
    setForm((f) => ({ ...f, customer: value }));
    if (reason === "input" && customerId) {
      setCustomerId(null);
      setLinkedCustomer(null);
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (!selected || !form.customer.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await issueLicense({
        product: selected.productId,
        type: selected.type,
        customer: form.customer,
        customerEmail: form.customerEmail,
        location: form.location,
        remarks: form.remarks,
        customerId,
        saveCustomer: !customerId && saveCustomer,
        ...(onBehalf ? { distributorId: distributor.id } : {}),
      });
      setIssued({
        ...result,
        customer: form.customer.trim(),
        customerEmail: form.customerEmail.trim(),
      });
    } catch (e) {
      console.error(e);
      setError(describeIssueError(e));
    } finally {
      setBusy(false);
    }
  }

  function issueAnother() {
    setIssued(null);
    setForm(EMPTY);
    setCustomerId(null);
    setLinkedCustomer(null);
    setSaveCustomer(true);
    setCopied(false);
  }

  const message =
    issued && shareText({ ...issued, distributorName: distributor?.name });

  async function copyMessage() {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
    } catch (e) {
      console.error("Clipboard unavailable", e);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      {issued ? (
        <>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon color="success" /> Key issued
          </DialogTitle>
          <DialogContent>
            <Typography color="text.secondary" gutterBottom>
              {productName(issued.product)} · {issued.type} for{" "}
              <strong>{issued.customer}</strong>
            </Typography>
            <Box
              sx={{
                my: 2,
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                bgcolor: brand.sand,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <KeyText value={issued.licenseKey} large />
            </Box>
            <Typography variant="subtitle2" gutterBottom>
              Send it to your customer
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ContentCopyIcon />}
                onClick={copyMessage}
              >
                {copied ? "Message copied" : "Copy message"}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<EmailIcon />}
                href={`mailto:${encodeURIComponent(issued.customerEmail || "")}?subject=${encodeURIComponent(
                  `Your ${productName(issued.product)} licence key`,
                )}&body=${encodeURIComponent(message)}`}
              >
                Email
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<WhatsAppIcon />}
                href={`https://wa.me/?text=${encodeURIComponent(message)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp
              </Button>
            </Box>
            <Alert severity="info" sx={{ mt: 3 }}>
              {issued.remaining} {productName(issued.product)} {issued.type} key
              {issued.remaining === 1 ? "" : "s"} left. The key is also saved
              under My keys.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button
              color="inherit"
              onClick={issueAnother}
              disabled={available.length === 0}
            >
              Issue another
            </Button>
            <Button variant="contained" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <Box component="form" onSubmit={submit}>
          <DialogTitle>Issue a licence key</DialogTitle>
          <DialogContent>
            {onBehalf && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Issuing on behalf of <strong>{distributor?.name}</strong>. The
                key counts against their quota.
              </Alert>
            )}
            {available.length === 0 ? (
              <Alert severity="warning">
                No keys left to issue.{" "}
                {onBehalf
                  ? "Allocate more keys first."
                  : "Contact LiFE to top up your quota."}
              </Alert>
            ) : (
              <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
                <TextField
                  select
                  label="Key type"
                  value={selected?.bucket || ""}
                  onChange={(e) => setBucket(e.target.value)}
                  required
                >
                  {available.map((entry) => (
                    <MenuItem key={entry.bucket} value={entry.bucket}>
                      {entry.productName} · {entry.type} — {entry.remaining}{" "}
                      left
                    </MenuItem>
                  ))}
                </TextField>
                <Box>
                  <Autocomplete
                    freeSolo
                    options={customerOptions}
                    filterOptions={(x) => x}
                    value={linkedCustomer}
                    inputValue={form.customer}
                    onChange={pickCustomer}
                    onInputChange={typeCustomer}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.name || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderOption={renderCustomerOption}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer name"
                        placeholder="Type a name, or pick a saved customer"
                        required
                        autoFocus
                      />
                    )}
                  />
                  {linkedCustomer ? (
                    <Typography variant="caption" color="text.secondary">
                      Linked to your saved customer {linkedCustomer.name}.
                    </Typography>
                  ) : (
                    form.customer.trim() && (
                      <FormControlLabel
                        sx={{ mt: 0.25 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={saveCustomer}
                            onChange={(e) => setSaveCustomer(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Save to my customers
                          </Typography>
                        }
                      />
                    )
                  )}
                </Box>
                <TextField
                  label="Customer email (optional)"
                  type="email"
                  value={form.customerEmail}
                  onChange={set("customerEmail")}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
                <TextField
                  label="Project / location (optional)"
                  value={form.location}
                  onChange={set("location")}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
                <TextField
                  label="Remarks (optional)"
                  value={form.remarks}
                  onChange={set("remarks")}
                  multiline
                  minRows={2}
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                />
                <Typography variant="caption" color="text.secondary">
                  A new key is created when you press Issue and counts against
                  your quota straight away.
                </Typography>
              </Box>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button color="inherit" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={busy || !selected || !form.customer.trim()}
            >
              {busy ? "Issuing…" : "Issue key"}
            </Button>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
}
